// Shared "last seen" / online-vs-offline helpers for sensors.
//
// A sensor is considered offline when its last uplink (SensorInfo.LastRx) is
// older than SENSOR_OFFLINE_THRESHOLD_MS. Devices that have never reported
// (no/blank LastRx) are treated as offline too.

export const SENSOR_OFFLINE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour

export const SENSOR_ONLINE_COLOR = '#1976d2'; // blue
// Offline uses grey for text/status so it reads as inactive, and a separate
// red for the card border (dashed) so it stays distinct from solid-red alarms.
export const SENSOR_OFFLINE_COLOR = '#9e9e9e'; // grey — labels, dots, muted text
export const SENSOR_OFFLINE_BORDER_COLOR = '#e74c3c'; // red — dashed card outline

export const parseLastRxMs = (lastRx?: string | null): number | null => {
  if (!lastRx) return null;
  const t = new Date(lastRx).getTime();
  return Number.isFinite(t) ? t : null;
};

export const isSensorOffline = (lastRx?: string | null, nowMs: number = Date.now()): boolean => {
  const t = parseLastRxMs(lastRx);
  if (t === null) return true;
  return nowMs - t > SENSOR_OFFLINE_THRESHOLD_MS;
};

// Human-friendly "time since last uplink", e.g. "just now", "5 min ago",
// "3 hours ago", "2 days ago", "4 months ago", "never".
export const formatLastSeen = (lastRx?: string | null, nowMs: number = Date.now()): string => {
  const t = parseLastRxMs(lastRx);
  if (t === null) return 'never';

  const sec = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (sec < 45) return 'just now';

  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;

  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;

  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};
